import { keyframes, styled } from '@mui/material';
import { usePrefersReducedMotion, useRandomInterval } from '@shared/hooks';
import { createNumRange, randomNumberInRange } from '@shared/utils';
import React, { PropsWithChildren } from 'react';

const DEFAULT_COLOR = '#FFC700';

const generateSparkle = (color: string) => {
  return {
    id: String(randomNumberInRange(10000, 99999)),
    createdAt: Date.now(),
    color,
    size: randomNumberInRange(10, 20),
    style: {
      top: randomNumberInRange(-10, 80) + '%',
      left: randomNumberInRange(-10, 100) + '%',
    },
  };
};

interface SparklesProps extends PropsWithChildren {
  color?: string;
}

const Sparkles: React.FC<SparklesProps> = ({ color = DEFAULT_COLOR, children, ...delegated }) => {
  const [sparkles, setSparkles] = React.useState(() => {
    return createNumRange(3).map(() => generateSparkle(color));
  });

  const prefersReducedMotion = usePrefersReducedMotion();

  useRandomInterval(
    () => {
      const sparkle = generateSparkle(color);

      const now = Date.now();

      const nextSparkles = sparkles.filter((sp) => {
        const delta = now - sp.createdAt;
        return delta < 1500;
      });

      nextSparkles.push(sparkle);

      setSparkles(nextSparkles);
    },
    prefersReducedMotion ? undefined : 500,
    prefersReducedMotion ? undefined : 1000
  );

  return (
    <Wrapper {...delegated}>
      {sparkles.map((sparkle) => (
        <Sparkle key={sparkle.id} color={sparkle.color} size={sparkle.size} style={sparkle.style} />
      ))}
      <ChildWrapper>{children}</ChildWrapper>
    </Wrapper>
  );
};

interface SparkleProps {
  size?: number;
  color?: string;
  style?: React.CSSProperties;
}

const Sparkle: React.FC<SparkleProps> = ({ size, color, style }) => {
  return (
    <SparkleWrapper style={style}>
      <SparkleSvg width={size} height={size} viewBox="0 0 68 68" fill="none">
        <path
          d="M26.5 25.5C19.0043 33.3697 0 34 0 34C0 34 19.1013 35.3684 26.5 43.5C33.234 50.901 34 68 34 68C34 68 36.9884 50.7065 44.5 43.5C51.6431 36.647 68 34 68 34C68 34 51.6947 32.0939 44.5 25.5C36.5605 18.2235 34 0 34 0C34 0 33.6591 17.9837 26.5 25.5Z"
          fill={color}
        />
      </SparkleSvg>
    </SparkleWrapper>
  );
};

const comeInOut = keyframes`
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1);
  }
  100% {
    transform: scale(0);
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(180deg);
  }
`;

const Wrapper = styled('span')`
  display: inline-block;
  position: relative;
`;

const SparkleWrapper = styled('span')`
  position: absolute;
  display: block;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${comeInOut} 1500ms forwards;
  }
`;

const SparkleSvg = styled('svg')`
  display: block;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${spin} 2000ms linear;
  }
`;

const ChildWrapper = styled('strong')`
  position: relative;
  z-index: 1;
  font-weight: bold;
`;

export default Sparkles;
